export type WebColorPickerOptions = {
  label: string;
  value: string;
  onInput?: (value: string) => void;
};

type HsvColor = {
  h: number;
  s: number;
  v: number;
};

type SliderHandle = {
  row: HTMLElement;
  input: HTMLInputElement;
  value: HTMLElement;
};

const WHEEL_SIZE = 260;
const WHEEL_RADIUS = WHEEL_SIZE / 2;
const WHEEL_CENTER = WHEEL_SIZE / 2;
const MIN_VISIBLE_PICK_VALUE = 100;

export function createWebColorPicker(options: WebColorPickerOptions): HTMLElement {
  let currentColor = normalizeHex(options.value);
  let currentHsv = hexToHsv(currentColor);
  let wheelImageReady = false;

  const field = document.createElement("div");
  field.className = "ui-field";

  const label = document.createElement("span");
  label.className = "ui-label";
  label.textContent = options.label;

  const picker = document.createElement("div");
  picker.className = "web-color-picker";

  const preview = document.createElement("button");
  preview.type = "button";
  preview.className = "web-color-preview";
  preview.style.background = currentColor;
  preview.setAttribute("aria-label", options.label);

  const hexInput = document.createElement("input");
  hexInput.className = "web-color-hex";
  hexInput.type = "text";
  hexInput.value = currentColor;
  hexInput.maxLength = 7;
  hexInput.spellcheck = false;
  hexInput.autocomplete = "off";

  const backdrop = document.createElement("div");
  backdrop.className = "web-color-backdrop";
  backdrop.hidden = true;

  const popover = document.createElement("div");
  popover.className = "web-color-popover";
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-modal", "true");
  popover.setAttribute("aria-label", options.label);

  const header = document.createElement("div");
  header.className = "web-color-header";

  const title = document.createElement("div");
  title.className = "web-color-title";
  title.textContent = options.label;

  const close = document.createElement("button");
  close.type = "button";
  close.className = "web-color-close";
  close.textContent = "×";
  close.setAttribute("aria-label", "Close color picker");

  header.append(title, close);

  const wheelWrap = document.createElement("div");
  wheelWrap.className = "web-color-wheel-wrap";

  const wheel = document.createElement("canvas");
  wheel.className = "web-color-wheel";
  wheel.width = WHEEL_SIZE;
  wheel.height = WHEEL_SIZE;

  const wheelMarker = document.createElement("div");
  wheelMarker.className = "web-color-wheel-marker";

  wheelWrap.append(wheel, wheelMarker);

  const adjustments = document.createElement("div");
  adjustments.className = "web-color-adjustments";

  const brightness = createSlider("Brightness", 0, 100, currentHsv.v);

  brightness.input.addEventListener("input", () => {
    currentHsv = {
      ...currentHsv,
      v: Number(brightness.input.value)
    };

    setColor(hsvToHex(currentHsv), true);
  });

  adjustments.append(brightness.row);

  const bottomRow = document.createElement("div");
  bottomRow.className = "web-color-bottom-row";

  const selectedPreview = document.createElement("div");
  selectedPreview.className = "web-color-selected-preview";
  selectedPreview.style.background = currentColor;

  const selectedValue = document.createElement("div");
  selectedValue.className = "web-color-selected-value";
  selectedValue.textContent = currentColor;

  const done = document.createElement("button");
  done.type = "button";
  done.className = "web-color-action web-color-action-primary";
  done.textContent = "Done";

  done.addEventListener("click", closePicker);

  bottomRow.append(selectedPreview, selectedValue, done);

  popover.append(header, wheelWrap, adjustments, bottomRow);
  backdrop.append(popover);
  picker.append(preview, hexInput);
  field.append(label, picker);

  preview.addEventListener("click", (event) => {
    event.preventDefault();
    openPicker();
  });

  close.addEventListener("click", closePicker);

  backdrop.addEventListener("pointerdown", (event) => {
    if (event.target === backdrop) {
      closePicker();
    }
  });

  hexInput.addEventListener("input", () => {
    const normalized = normalizeHexInput(hexInput.value);

    if (normalized) {
      setColor(normalized, true);
    }
  });

  wheel.addEventListener("pointerdown", (event) => {
    wheel.setPointerCapture(event.pointerId);
    pickFromWheel(event);
  });

  wheel.addEventListener("pointermove", (event) => {
    if (event.buttons !== 1) {
      return;
    }

    pickFromWheel(event);
  });

  wheel.addEventListener("pointerup", (event) => {
    if (wheel.hasPointerCapture(event.pointerId)) {
      wheel.releasePointerCapture(event.pointerId);
    }
  });

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!backdrop.hidden && event.key === "Escape") {
      closePicker();
    }
  };

  const onResize = (): void => {
    if (!backdrop.hidden) {
      requestAnimationFrame(positionPopover);
    }
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onResize);
    window.visualViewport.addEventListener("scroll", onResize);
  }

  drawWheel();
  syncUi();

  return field;

  function openPicker(): void {
    syncFromInput();

    if (!document.body.contains(backdrop)) {
      document.body.append(backdrop);
    }

    backdrop.hidden = false;
    document.body.classList.add("web-color-modal-open");

    if (!wheelImageReady) {
      drawWheel();
    }

    syncUi();

    requestAnimationFrame(positionPopover);
  }

  function closePicker(): void {
    backdrop.hidden = true;
    document.body.classList.remove("web-color-modal-open");
  }

  function positionPopover(): void {
    const viewport = getViewport();
    const margin = 8;

    popover.style.setProperty("--web-color-popover-scale", "1");
    popover.style.left = "0px";
    popover.style.top = "0px";

    const naturalWidth = Math.max(1, popover.offsetWidth);
    const naturalHeight = Math.max(1, popover.scrollHeight);
    const landscapePhone = isLandscapePhoneViewport(viewport.width, viewport.height);

    if (landscapePhone) {
      const scale = clamp(
        Math.min(
          (viewport.width - margin * 2) / naturalWidth,
          (viewport.height - margin * 2) / naturalHeight
        ),
        0.45,
        1
      );
      const scaledWidth = naturalWidth * scale;
      const scaledHeight = naturalHeight * scale;
      const left = viewport.left + (viewport.width - scaledWidth) * 0.5;
      const top = viewport.top + (viewport.height - scaledHeight) * 0.5;

      popover.style.setProperty("--web-color-popover-scale", formatScale(scale));
      popover.style.left = `${Math.round(Math.max(viewport.left + margin, left))}px`;
      popover.style.top = `${Math.round(Math.max(viewport.top + margin, top))}px`;
      return;
    }

    const popoverRect = popover.getBoundingClientRect();
    const portraitPhone = isPortraitPhoneViewport(viewport.width, viewport.height);

    if (portraitPhone) {
      const left = viewport.left + (viewport.width - popoverRect.width) * 0.5;
      const top = viewport.top + Math.max(margin, (viewport.height - popoverRect.height) * 0.5);

      popover.style.left = `${Math.round(left)}px`;
      popover.style.top = `${Math.round(top)}px`;
      return;
    }

    const panel = document.querySelector(".menu-panel");
    const previewCard = document.querySelector(".player-preview-card");

    if (panel instanceof HTMLElement && previewCard instanceof HTMLElement) {
      const panelRect = panel.getBoundingClientRect();
      const previewRect = previewCard.getBoundingClientRect();

      const targetCenterX = previewRect.right + (panelRect.right - previewRect.right) * 0.5;
      const targetCenterY = panelRect.top + panelRect.height * 0.52;

      const left = clamp(
        Math.round(targetCenterX - popoverRect.width * 0.5),
        viewport.left + margin,
        Math.max(viewport.left + margin, viewport.left + viewport.width - popoverRect.width - margin)
      );

      const top = clamp(
        Math.round(targetCenterY - popoverRect.height * 0.5),
        viewport.top + margin,
        Math.max(viewport.top + margin, viewport.top + viewport.height - popoverRect.height - margin)
      );

      popover.style.left = `${left}px`;
      popover.style.top = `${top}px`;
      return;
    }

    const fallbackLeft = viewport.left + (viewport.width - popoverRect.width) * 0.5;
    const fallbackTop = viewport.top + (viewport.height - popoverRect.height) * 0.5;

    popover.style.left = `${Math.round(fallbackLeft)}px`;
    popover.style.top = `${Math.round(fallbackTop)}px`;
  }

  function setColor(color: string, emit: boolean): void {
    currentColor = normalizeHex(color);
    currentHsv = hexToHsv(currentColor);

    syncUi();

    if (emit) {
      options.onInput?.(currentColor);
    }
  }

  function pickFromWheel(event: PointerEvent): void {
    const rect = wheel.getBoundingClientRect();
    const scaleX = WHEEL_SIZE / rect.width;
    const scaleY = WHEEL_SIZE / rect.height;
    const x = (event.clientX - rect.left) * scaleX - WHEEL_CENTER;
    const y = (event.clientY - rect.top) * scaleY - WHEEL_CENTER;
    const distance = Math.sqrt(x * x + y * y);

    if (distance > WHEEL_RADIUS) {
      return;
    }

    const angle = Math.atan2(y, x);
    const hue = Math.round(((angle * 180) / Math.PI + 360) % 360);
    const saturation = Math.round(clamp((distance / WHEEL_RADIUS) * 100, 0, 100));
    const value = currentHsv.v <= 0 ? MIN_VISIBLE_PICK_VALUE : currentHsv.v;

    currentHsv = {
      h: hue,
      s: saturation,
      v: value
    };

    setColor(hsvToHex(currentHsv), true);
  }

  function syncUi(): void {
    preview.style.background = currentColor;
    selectedPreview.style.background = currentColor;
    selectedValue.textContent = currentColor;
    hexInput.value = currentColor;

    brightness.input.value = String(currentHsv.v);
    brightness.value.textContent = String(currentHsv.v);

    updateMarker();
  }

  function syncFromInput(): void {
    const normalized = normalizeHexInput(hexInput.value);

    if (!normalized || normalized === currentColor) {
      return;
    }

    currentColor = normalized;
    currentHsv = hexToHsv(currentColor);
  }

  function updateMarker(): void {
    const angle = (currentHsv.h * Math.PI) / 180;
    const distance = (currentHsv.s / 100) * WHEEL_RADIUS;
    const x = WHEEL_CENTER + Math.cos(angle) * distance;
    const y = WHEEL_CENTER + Math.sin(angle) * distance;

    wheelMarker.style.left = `${(x / WHEEL_SIZE) * 100}%`;
    wheelMarker.style.top = `${(y / WHEEL_SIZE) * 100}%`;
    wheelMarker.style.background = hsvToHex({
      h: currentHsv.h,
      s: currentHsv.s,
      v: 100
    });
  }

  function drawWheel(): void {
    const context = wheel.getContext("2d");

    if (!context) {
      return;
    }

    const image = context.createImageData(WHEEL_SIZE, WHEEL_SIZE);

    for (let y = 0; y < WHEEL_SIZE; y += 1) {
      for (let x = 0; x < WHEEL_SIZE; x += 1) {
        const dx = x - WHEEL_CENTER;
        const dy = y - WHEEL_CENTER;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const index = (y * WHEEL_SIZE + x) * 4;

        if (distance > WHEEL_RADIUS) {
          image.data[index] = 0;
          image.data[index + 1] = 0;
          image.data[index + 2] = 0;
          image.data[index + 3] = 0;
          continue;
        }

        const angle = Math.atan2(dy, dx);
        const h = ((angle * 180) / Math.PI + 360) % 360;
        const s = clamp((distance / WHEEL_RADIUS) * 100, 0, 100);
        const rgb = hsvToRgb({ h, s, v: 100 });

        image.data[index] = rgb.r;
        image.data[index + 1] = rgb.g;
        image.data[index + 2] = rgb.b;
        image.data[index + 3] = 255;
      }
    }

    context.putImageData(image, 0, 0);

    context.save();
    context.beginPath();
    context.arc(WHEEL_CENTER, WHEEL_CENTER, WHEEL_RADIUS - 0.5, 0, Math.PI * 2);
    context.strokeStyle = "rgba(0, 0, 0, 0.18)";
    context.lineWidth = 1;
    context.stroke();

    const highlight = context.createRadialGradient(
      WHEEL_CENTER * 0.72,
      WHEEL_CENTER * 0.62,
      WHEEL_RADIUS * 0.04,
      WHEEL_CENTER,
      WHEEL_CENTER,
      WHEEL_RADIUS
    );
    highlight.addColorStop(0, "rgba(255,255,255,0.28)");
    highlight.addColorStop(0.45, "rgba(255,255,255,0.04)");
    highlight.addColorStop(1, "rgba(0,0,0,0.04)");

    context.fillStyle = highlight;
    context.beginPath();
    context.arc(WHEEL_CENTER, WHEEL_CENTER, WHEEL_RADIUS, 0, Math.PI * 2);
    context.fill();
    context.restore();

    wheelImageReady = true;
  }
}

function createSlider(
  labelText: string,
  min: number,
  max: number,
  initial: number
): SliderHandle {
  const row = document.createElement("label");
  row.className = "web-color-slider-row";

  const label = document.createElement("span");
  label.className = "web-color-mini-label";
  label.textContent = labelText;

  const input = document.createElement("input");
  input.className = "web-color-range";
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.value = String(initial);

  const value = document.createElement("span");
  value.className = "web-color-range-value";
  value.textContent = String(initial);

  row.append(label, input, value);
  return { row, input, value };
}

function normalizeHex(value: string): string {
  const input = value.trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(input) ? input : "#4287f5";
}

function normalizeHexInput(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;

  if (/^#[0-9a-f]{6}$/.test(withHash)) {
    return withHash;
  }

  return null;
}

function hexToHsv(hex: string): HsvColor {
  const normalized = normalizeHex(hex).replace("#", "");
  const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;

  if (delta !== 0) {
    if (max === r) {
      h = 60 * (((g - b) / delta) % 6);
    } else if (max === g) {
      h = 60 * ((b - r) / delta + 2);
    } else {
      h = 60 * ((r - g) / delta + 4);
    }
  }

  if (h < 0) {
    h += 360;
  }

  const s = max === 0 ? 0 : delta / max;
  const v = max;

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
}

function hsvToHex(color: HsvColor): string {
  const rgb = hsvToRgb(color);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

function hsvToRgb(color: HsvColor): { r: number; g: number; b: number } {
  const h = (((color.h % 360) + 360) % 360) / 60;
  const s = clamp(color.s, 0, 100) / 100;
  const v = clamp(color.v, 0, 100) / 100;

  const c = v * s;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = v - c;

  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h >= 0 && h < 1) {
    rp = c;
    gp = x;
  } else if (h >= 1 && h < 2) {
    rp = x;
    gp = c;
  } else if (h >= 2 && h < 3) {
    gp = c;
    bp = x;
  } else if (h >= 3 && h < 4) {
    gp = x;
    bp = c;
  } else if (h >= 4 && h < 5) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }

  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    b: Math.round((bp + m) * 255)
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function toHex(value: number): string {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
}

function getViewport(): { width: number; height: number; left: number; top: number } {
  const visualViewport = window.visualViewport;

  if (visualViewport) {
    return {
      width: visualViewport.width,
      height: visualViewport.height,
      left: visualViewport.offsetLeft,
      top: visualViewport.offsetTop
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    left: 0,
    top: 0
  };
}

function isLandscapePhoneViewport(width: number, height: number): boolean {
  const shortestSide = Math.min(width, height);
  const longestSide = Math.max(width, height);

  return width > height && shortestSide <= 560 && longestSide <= 1100;
}

function isPortraitPhoneViewport(width: number, height: number): boolean {
  const shortestSide = Math.min(width, height);
  const longestSide = Math.max(width, height);

  return height >= width && shortestSide <= 560 && longestSide <= 1100;
}

function formatScale(value: number): string {
  return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}